import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = 'https://iia-backend-production.up.railway.app';

const AdminCourseManager = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [units, setUnits] = useState([]);
  const [expandedUnit, setExpandedUnit] = useState(null);
  const [expandedModule, setExpandedModule] = useState(null);
  const [modules, setModules] = useState({});
  const [tasks, setTasks] = useState({});
  const [message, setMessage] = useState('');

  // New item forms
  const [newUnit, setNewUnit] = useState({ title: '', description: '' });
  const [newModule, setNewModule] = useState({ 
    title: '', description: '', drip_days: 0 
  });
  const [newTask, setNewTask] = useState({ 
    title: '', task_type: 'lecture' 
  });

  // Which forms are showing
  const [showUnitForm, setShowUnitForm] = useState(false);
  const [showModuleForm, setShowModuleForm] = useState(null);
  const [showTaskForm, setShowTaskForm] = useState(null);
  const [showQuizBuilder, setShowQuizBuilder] = useState(null);

  useEffect(() => {
    fetchCourse();
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const [courseRes, unitsRes] = await Promise.all([
        axios.get(`${API_URL}/api/instructional/courses/${courseId}`),
        axios.get(`${API_URL}/api/instructional/courses/${courseId}/units`)
      ]);
      setCourse(courseRes.data.course);
      setUnits(unitsRes.data.units);
    } catch (err) {
      console.error('Fetch error:', err);
    }
  };

  const fetchModules = async (unitId) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/instructional/units/${unitId}/modules`
      );
      setModules(prev => ({ ...prev, [unitId]: res.data.modules }));
    } catch (err) {
      console.error('Fetch modules error:', err);
    }
  };

  const fetchTasks = async (moduleId) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/instructional/modules/${moduleId}/tasks`
      );
      setTasks(prev => ({ ...prev, [moduleId]: res.data.tasks }));
    } catch (err) {
      console.error('Fetch tasks error:', err);
    }
  };

  const handleExpandUnit = (unitId) => {
    if (expandedUnit === unitId) {
      setExpandedUnit(null);
    } else {
      setExpandedUnit(unitId);
      fetchModules(unitId);
    }
  };

  const handleExpandModule = (moduleId) => {
    if (expandedModule === moduleId) {
      setExpandedModule(null);
    } else {
      setExpandedModule(moduleId);
      fetchTasks(moduleId);
    }
  };

  const handleCreateUnit = async () => {
    try {
      await axios.post(`${API_URL}/api/instructional/units`, {
        course_id: courseId,
        title: newUnit.title,
        description: newUnit.description,
        order_index: units.length + 1
      });
      setMessage('Unit created');
      setNewUnit({ title: '', description: '' });
      setShowUnitForm(false);
      fetchCourse();
    } catch (err) {
      setMessage('Error creating unit');
    }
  };

  const handleCreateModule = async (unitId) => {
    try {
      const unitModules = modules[unitId] || [];
      await axios.post(`${API_URL}/api/instructional/modules`, {
        unit_id: unitId,
        title: newModule.title,
        description: newModule.description,
        drip_days: newModule.drip_days,
        order_index: unitModules.length + 1
      });
      setMessage('Module created');
      setNewModule({ title: '', description: '', drip_days: 0 });
      setShowModuleForm(null);
      fetchModules(unitId);
    } catch (err) {
      setMessage('Error creating module');
    }
  };

  const handleCreateTask = async (moduleId) => {
    try {
      const moduleTasks = tasks[moduleId] || [];
      await axios.post(`${API_URL}/api/instructional/tasks`, {
        module_id: moduleId,
        title: newTask.title,
        task_type: newTask.task_type,
        order_index: moduleTasks.length + 1,
        content: {}
      });
      setMessage('Task created');
      setNewTask({ title: '', task_type: 'lecture' });
      setShowTaskForm(null);
      fetchTasks(moduleId);
    } catch (err) {
      setMessage('Error creating task');
    }
  };

  const handleDeleteUnit = async (unitId) => {
    if (!window.confirm('Delete this unit and all its content?')) return;
    try {
      await axios.delete(`${API_URL}/api/instructional/units/${unitId}`);
      setMessage('Unit deleted');
      fetchCourse();
    } catch (err) {
      setMessage('Error deleting unit');
    }
  };

  const handleDeleteModule = async (moduleId, unitId) => {
    if (!window.confirm('Delete this module and all its tasks?')) return;
    try {
      await axios.delete(`${API_URL}/api/instructional/modules/${moduleId}`);
      setMessage('Module deleted');
      fetchModules(unitId);
    } catch (err) {
      setMessage('Error deleting module');
    }
  };

  const handleDeleteTask = async (taskId, moduleId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axios.delete(`${API_URL}/api/instructional/tasks/${taskId}`);
      setMessage('Task deleted');
      fetchTasks(moduleId);
    } catch (err) {
      setMessage('Error deleting task');
    }
  };

  const taskTypeLabel = (type) => {
    const types = {
      lecture: '🎥 Lecture',
      workbook: '📓 Workbook',
      discussion: '💬 Discussion',
      live_discussion: '🎙️ Live Discussion',
      live_practice: '🤝 Live Practice',
      quiz: '📝 Quiz'
    };
    return types[type] || type;
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.logo}>IIA</h1>
          <span style={styles.logoText}>Admin Panel</span>
        </div>
        <button 
          style={styles.returnBtn} 
          onClick={() => navigate('/admin')}
        >
          ← Back to Admin Panel
        </button>
      </div>

      <div style={styles.main}>
        {/* Course Header */}
        <div style={styles.courseHeader}>
          <h2 style={styles.pageTitle}>📚 {course?.title}</h2>
          <p style={styles.pageSubtitle}>{course?.description}</p>
          <div style={styles.courseActions}>
            <span style={course?.is_published ? 
              styles.badgePublished : styles.badgeDraft}>
              {course?.is_published ? 'Published' : 'Draft'}
            </span>
          </div>
        </div>

        {message && (
          <div style={styles.message}>{message}</div>
        )}

        {/* Units Section */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>
              Units ({units.length})
            </h3>
            <button 
              style={styles.addBtn}
              onClick={() => setShowUnitForm(!showUnitForm)}
            >
              + Add Unit
            </button>
          </div>

          {/* New Unit Form */}
          {showUnitForm && (
            <div style={styles.inlineForm}>
              <input
                style={styles.input}
                placeholder="Unit title"
                value={newUnit.title}
                onChange={e => setNewUnit({...newUnit, title: e.target.value})}
              />
              <input
                style={styles.input}
                placeholder="Unit description (optional)"
                value={newUnit.description}
                onChange={e => setNewUnit({
                  ...newUnit, description: e.target.value
                })}
              />
              <div style={styles.formActions}>
                <button style={styles.saveBtn} onClick={handleCreateUnit}>
                  Save Unit
                </button>
                <button 
                  style={styles.cancelBtn} 
                  onClick={() => setShowUnitForm(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Units List */}
          {units.length === 0 && (
            <p style={styles.emptyText}>No units yet — add your first unit</p>
          )}

          {units.map((unit, unitIndex) => (
            <div key={unit.id} style={styles.unitCard}>
              {/* Unit Header */}
              <div style={styles.unitHeader}>
                <div style={styles.unitHeaderLeft}>
                  <span style={styles.indexBadge}>
                    Unit {unitIndex + 1}
                  </span>
                  <div>
                    <h4 style={styles.unitTitle}>{unit.title}</h4>
                    {unit.description && (
                      <p style={styles.unitDesc}>{unit.description}</p>
                    )}
                  </div>
                </div>
                <div style={styles.unitHeaderRight}>
                  <button
                    style={styles.expandBtn}
                    onClick={() => handleExpandUnit(unit.id)}
                  >
                    {expandedUnit === unit.id ? '▲ Hide' : '▼ Show Modules'}
                  </button>
                  <button
                    style={styles.deleteBtn}
                    onClick={() => handleDeleteUnit(unit.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Modules — shown when unit is expanded */}
              {expandedUnit === unit.id && (
                <div style={styles.modulesContainer}>
                  <div style={styles.modulesHeader}>
                    <span style={styles.modulesTitle}>
                      Modules ({(modules[unit.id] || []).length})
                    </span>
                    <button
                      style={styles.addBtnSmall}
                      onClick={() => setShowModuleForm(
                        showModuleForm === unit.id ? null : unit.id
                      )}
                    >
                      + Add Module
                    </button>
                  </div>

                  {/* New Module Form */}
                  {showModuleForm === unit.id && (
                    <div style={styles.inlineFormSmall}>
                      <input
                        style={styles.inputSmall}
                        placeholder="Module title"
                        value={newModule.title}
                        onChange={e => setNewModule({
                          ...newModule, title: e.target.value
                        })}
                      />
                      <input
                        style={styles.inputSmall}
                        placeholder="Description (optional)"
                        value={newModule.description}
                        onChange={e => setNewModule({
                          ...newModule, description: e.target.value
                        })}
                      />
                      <div style={styles.dripRow}>
                        <label style={styles.dripLabel}>
                          Drip after days:
                        </label>
                        <input
                          style={styles.dripInput}
                          type="number"
                          min="0"
                          value={newModule.drip_days}
                          onChange={e => setNewModule({
                            ...newModule, drip_days: e.target.value
                          })}
                        />
                      </div>
                      <div style={styles.formActions}>
                        <button 
                          style={styles.saveBtn}
                          onClick={() => handleCreateModule(unit.id)}
                        >
                          Save Module
                        </button>
                        <button 
                          style={styles.cancelBtn}
                          onClick={() => setShowModuleForm(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Modules List */}
                  {(modules[unit.id] || []).length === 0 && (
                    <p style={styles.emptyTextSmall}>
                      No modules yet
                    </p>
                  )}

                  {(modules[unit.id] || []).map((mod, modIndex) => (
                    <div key={mod.id} style={styles.moduleCard}>
                      {/* Module Header */}
                      <div style={styles.moduleHeader}>
                        <div style={styles.moduleHeaderLeft}>
                          <span style={styles.moduleIndexBadge}>
                            Module {modIndex + 1}
                          </span>
                          <div>
                            <h5 style={styles.moduleTitle}>{mod.title}</h5>
                            <span style={styles.dripBadge}>
                              🕐 Drip: Day {mod.drip_days}
                            </span>
                          </div>
                        </div>
                        <div style={styles.moduleHeaderRight}>
                          <button
                            style={styles.expandBtnSmall}
                            onClick={() => handleExpandModule(mod.id)}
                          >
                            {expandedModule === mod.id ? 
                              '▲ Hide' : '▼ Tasks'}
                          </button>
                          <button
                            style={styles.quizBuilderBtn}
                            onClick={() => setShowQuizBuilder(
                              showQuizBuilder === mod.id ? null : mod.id
                            )}
                          >
                            📝 Quiz
                          </button>
                          <button
                            style={styles.deleteBtnSmall}
                            onClick={() => handleDeleteModule(mod.id, unit.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      {/* Tasks — shown when module is expanded */}
                      {expandedModule === mod.id && (
                        <div style={styles.tasksContainer}>
                          <div style={styles.tasksHeader}>
                            <span style={styles.tasksTitle}>
                              Tasks ({(tasks[mod.id] || []).length})
                            </span>
                            <button
                              style={styles.addBtnTiny}
                              onClick={() => setShowTaskForm(
                                showTaskForm === mod.id ? null : mod.id
                              )}
                            >
                              + Add Task
                            </button>
                          </div>

                          {/* New Task Form */}
                          {showTaskForm === mod.id && (
                            <div style={styles.inlineFormTiny}>
                              <input
                                style={styles.inputSmall}
                                placeholder="Task title"
                                value={newTask.title}
                                onChange={e => setNewTask({
                                  ...newTask, title: e.target.value
                                })}
                              />
                              <select
                                style={styles.inputSmall}
                                value={newTask.task_type}
                                onChange={e => setNewTask({
                                  ...newTask, task_type: e.target.value
                                })}
                              >
                                <option value="lecture">🎥 Lecture</option>
                                <option value="workbook">📓 Workbook</option>
                                <option value="discussion">
                                  💬 Discussion Board
                                </option>
                                <option value="live_discussion">
                                  🎙️ Live Discussion
                                </option>
                                <option value="live_practice">
                                  🤝 Live Practice
                                </option>
                                <option value="quiz">📝 Quiz</option>
                              </select>
                              <div style={styles.formActions}>
                                <button
                                  style={styles.saveBtn}
                                  onClick={() => handleCreateTask(mod.id)}
                                >
                                  Save Task
                                </button>
                                <button
                                  style={styles.cancelBtn}
                                  onClick={() => setShowTaskForm(null)}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Tasks List */}
                          {(tasks[mod.id] || []).length === 0 && (
                            <p style={styles.emptyTextTiny}>No tasks yet</p>
                          )}

                          {(tasks[mod.id] || []).map((task, taskIndex) => (
                            <div key={task.id} style={styles.taskRow}>
                              <div style={styles.taskRowLeft}>
                                <span style={styles.taskIndex}>
                                  {taskIndex + 1}
                                </span>
                                <span style={styles.taskType}>
                                  {taskTypeLabel(task.task_type)}
                                </span>
                                <span style={styles.taskTitle}>
                                  {task.title}
                                </span>
                              </div>
                              <div style={styles.taskRowRight}>
                                <button
                                  style={styles.editTaskBtn}
                                  onClick={() => navigate(
                                    `/admin/task/${task.id}`
                                  )}
                                >
                                  Edit Content →
                                </button>
                                <button
                                  style={styles.deleteBtnTiny}
                                  onClick={() => handleDeleteTask(
                                    task.id, mod.id
                                  )}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          ))}

                          {/* Quiz Builder */}
                          {showQuizBuilder === mod.id && (
                            <QuizBuilder
                              moduleId={mod.id}
                              modulePassing={mod.passing_score || 80}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1A2744',
    color: '#ffffff',
  },
  header: {
    backgroundColor: '#0f1a33',
    borderBottom: '1px solid #C49A28',
    padding: '16px 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logo: {
    color: '#C49A28',
    fontSize: '24px',
    fontWeight: '800',
    margin: '0',
    letterSpacing: '2px',
  },
  logoText: {
    color: '#ffffff80',
    fontSize: '14px',
    letterSpacing: '1px',
  },
  returnBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #C49A28',
    color: '#C49A28',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  main: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '32px',
  },
  courseHeader: {
    marginBottom: '32px',
  },
  pageTitle: {
    color: '#C49A28',
    fontSize: '28px',
    fontWeight: '700',
    margin: '0 0 8px 0',
  },
  pageSubtitle: {
    color: '#ffffff60',
    fontSize: '14px',
    margin: '0 0 12px 0',
  },
  courseActions: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  badgePublished: {
    backgroundColor: '#1E844920',
    color: '#1E8449',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '600',
  },
  badgeDraft: {
    backgroundColor: '#ffffff10',
    color: '#ffffff60',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '600',
  },
  message: {
    backgroundColor: '#1E8449',
    color: '#ffffff',
    padding: '12px 16px',
    borderRadius: '6px',
    marginBottom: '24px',
    fontSize: '14px',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitle: {
    color: '#ffffff',
    fontSize: '18px',
    fontWeight: '600',
    margin: '0',
  },
  addBtn: {
    backgroundColor: '#C49A28',
    color: '#1A2744',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  addBtnSmall: {
    backgroundColor: '#1A6B8A',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    padding: '6px 14px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  addBtnTiny: {
    backgroundColor: '#1E8449',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '11px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  inlineForm: {
    backgroundColor: '#0f1a33',
    border: '1px solid #C49A2840',
    borderRadius: '8px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  inlineFormSmall: {
    backgroundColor: '#1A2744',
    border: '1px solid #ffffff15',
    borderRadius: '8px',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    margin: '8px 0',
  },
  inlineFormTiny: {
    backgroundColor: '#0f1a33',
    border: '1px solid #ffffff10',
    borderRadius: '6px',
    padding: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    margin: '6px 0',
  },
  input: {
    backgroundColor: '#1A2744',
    border: '1px solid #ffffff20',
    borderRadius: '6px',
    padding: '10px 14px',
    color: '#ffffff',
    fontSize: '14px',
    outline: 'none',
  },
  inputSmall: {
    backgroundColor: '#1A2744',
    border: '1px solid #ffffff20',
    borderRadius: '6px',
    padding: '8px 12px',
    color: '#ffffff',
    fontSize: '13px',
    outline: 'none',
  },
  dripRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  dripLabel: {
    color: '#ffffff80',
    fontSize: '13px',
  },
  dripInput: {
    backgroundColor: '#1A2744',
    border: '1px solid #ffffff20',
    borderRadius: '6px',
    padding: '6px 10px',
    color: '#ffffff',
    fontSize: '13px',
    outline: 'none',
    width: '80px',
  },
  formActions: {
    display: 'flex',
    gap: '8px',
  },
  saveBtn: {
    backgroundColor: '#C49A28',
    color: '#1A2744',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  cancelBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #ffffff30',
    color: '#ffffff60',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '13px',
    cursor: 'pointer',
  },
  emptyText: {
    color: '#ffffff30',
    fontSize: '14px',
    padding: '16px 0',
  },
  emptyTextSmall: {
    color: '#ffffff30',
    fontSize: '13px',
    padding: '8px 0',
  },
  emptyTextTiny: {
    color: '#ffffff30',
    fontSize: '12px',
    padding: '6px 0',
  },
  unitCard: {
    backgroundColor: '#0f1a33',
    border: '1px solid #ffffff15',
    borderRadius: '10px',
    overflow: 'hidden',
  },
  unitHeader: {
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  unitHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  indexBadge: {
    backgroundColor: '#C49A2820',
    color: '#C49A28',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '700',
    whiteSpace: 'nowrap',
  },
  unitTitle: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '600',
    margin: '0 0 2px 0',
  },
  unitDesc: {
    color: '#ffffff60',
    fontSize: '13px',
    margin: '0',
  },
  unitHeaderRight: {
    display: 'flex',
    gap: '8px',
  },
  expandBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #ffffff20',
    color: '#ffffff80',
    padding: '6px 14px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  deleteBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #ff000030',
    color: '#ff6666',
    padding: '6px 14px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  modulesContainer: {
    borderTop: '1px solid #ffffff10',
    padding: '16px 24px',
    backgroundColor: '#0d1628',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  modulesHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  modulesTitle: {
    color: '#ffffff80',
    fontSize: '13px',
    fontWeight: '600',
  },
  moduleCard: {
    backgroundColor: '#1A2744',
    border: '1px solid #ffffff10',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  moduleHeader: {
    padding: '14px 18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  moduleHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  moduleIndexBadge: {
    backgroundColor: '#1A6B8A20',
    color: '#1A6B8A',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '700',
    whiteSpace: 'nowrap',
  },
  moduleTitle: {
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '600',
    margin: '0 0 2px 0',
  },
  dripBadge: {
    color: '#ffffff40',
    fontSize: '11px',
  },
  moduleHeaderRight: {
    display: 'flex',
    gap: '6px',
  },
  expandBtnSmall: {
    backgroundColor: 'transparent',
    border: '1px solid #ffffff15',
    color: '#ffffff60',
    padding: '4px 10px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '11px',
  },
  deleteBtnSmall: {
    backgroundColor: 'transparent',
    border: '1px solid #ff000025',
    color: '#ff666680',
    padding: '4px 10px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '11px',
  },
  
  tasksContainer: {
    borderTop: '1px solid #ffffff08',
    padding: '12px 18px',
    backgroundColor: '#111e35',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  tasksHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tasksTitle: {
    color: '#ffffff60',
    fontSize: '12px',
    fontWeight: '600',
  },
  taskRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#1A2744',
    border: '1px solid #ffffff08',
    borderRadius: '6px',
    padding: '10px 14px',
  },
  taskRowLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  taskIndex: {
    color: '#ffffff30',
    fontSize: '11px',
    fontWeight: '700',
    minWidth: '16px',
  },
  taskType: {
    color: '#1A6B8A',
    fontSize: '12px',
    fontWeight: '600',
  },
  taskTitle: {
    color: '#ffffff',
    fontSize: '13px',
  },
  taskRowRight: {
    display: 'flex',
    gap: '6px',
  },
  editTaskBtn: {
    backgroundColor: '#1A6B8A',
    color: '#ffffff',
    border: 'none',
    padding: '4px 10px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: '600',
  },
  deleteBtnTiny: {
    backgroundColor: 'transparent',
    border: '1px solid #ff000020',
    color: '#ff666670',
    padding: '4px 8px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '11px',
  },
};
// ==================== QUIZ BUILDER ====================
const QuizBuilder = ({ moduleId, modulePassing }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [passingScore, setPassingScore] = useState(modulePassing || 80);
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({
    question_text: '',
    question_type: 'multiple_choice',
    answers: [
      { answer_text: '', is_correct: false },
      { answer_text: '', is_correct: false },
      { answer_text: '', is_correct: false },
      { answer_text: '', is_correct: false },
    ]
  });

  useEffect(() => { fetchQuestions(); }, [moduleId]);

  const fetchQuestions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/quiz/modules/${moduleId}/questions`);
      setQuestions(res.data.questions);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      question_text: '',
      question_type: 'multiple_choice',
      answers: [
        { answer_text: '', is_correct: false },
        { answer_text: '', is_correct: false },
        { answer_text: '', is_correct: false },
        { answer_text: '', is_correct: false },
      ]
    });
    setEditingQuestion(null);
  };

  const handleTypeChange = (type) => {
    if (type === 'true_false') {
      setForm({
        ...form,
        question_type: 'true_false',
        answers: [
          { answer_text: 'True', is_correct: true },
          { answer_text: 'False', is_correct: false },
        ]
      });
    } else {
      setForm({
        ...form,
        question_type: 'multiple_choice',
        answers: [
          { answer_text: '', is_correct: false },
          { answer_text: '', is_correct: false },
          { answer_text: '', is_correct: false },
          { answer_text: '', is_correct: false },
        ]
      });
    }
  };

  const handleCorrectAnswer = (index) => {
    setForm({
      ...form,
      answers: form.answers.map((a, i) => ({
        ...a,
        is_correct: i === index
      }))
    });
  };

  const handleSaveQuestion = async () => {
    if (!form.question_text.trim()) {
      setMessage('Please enter a question.');
      return;
    }
    const hasCorrect = form.answers.some(a => a.is_correct);
    if (!hasCorrect) {
      setMessage('Please mark one answer as correct.');
      return;
    }
    const hasAllAnswers = form.answers.every(a => a.answer_text.trim());
    if (!hasAllAnswers) {
      setMessage('Please fill in all answer options.');
      return;
    }

    try {
      if (editingQuestion) {
        await axios.patch(
          `${API_URL}/api/quiz/questions/${editingQuestion}`,
          { question_text: form.question_text, answers: form.answers }
        );
        setMessage('Question updated');
      } else {
        await axios.post(
          `${API_URL}/api/quiz/modules/${moduleId}/questions`,
          { ...form, order_index: questions.length }
        );
        setMessage('Question added');
      }
      resetForm();
      setShowForm(false);
      fetchQuestions();
    } catch (err) {
      setMessage('Error saving question');
    }
  };

  const handleEditQuestion = (question) => {
    setForm({
      question_text: question.question_text,
      question_type: question.question_type,
      answers: question.answers.map(a => ({
        answer_text: a.answer_text,
        is_correct: a.is_correct,
      }))
    });
    setEditingQuestion(question.id);
    setShowForm(true);
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await axios.delete(
        `${API_URL}/api/quiz/questions/${questionId}/module/${moduleId}`
      );
      fetchQuestions();
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdatePassingScore = async () => {
    try {
      await axios.patch(
        `${API_URL}/api/quiz/modules/${moduleId}/passing-score`,
        { passing_score: passingScore }
      );
      setMessage('Passing score updated');
    } catch (err) {
      setMessage('Error updating passing score');
    }
  };

  return (
    <div style={quizStyles.container}>
      <div style={quizStyles.header}>
        <h4 style={quizStyles.title}>📝 Quiz Builder</h4>
        <div style={quizStyles.headerRight}>
          <div style={quizStyles.passingScoreRow}>
            <label style={quizStyles.passingLabel}>Passing Score:</label>
            <input
              style={quizStyles.passingInput}
              type="number"
              min="1"
              max="100"
              value={passingScore}
              onChange={e => setPassingScore(parseInt(e.target.value))}
            />
            <span style={quizStyles.passingPct}>%</span>
            <button style={quizStyles.savePassingBtn} onClick={handleUpdatePassingScore}>
              Save
            </button>
          </div>
          <button
            style={quizStyles.addBtn}
            onClick={() => { resetForm(); setShowForm(!showForm); }}
          >
            + Add Question
          </button>
        </div>
      </div>

      {message && <p style={quizStyles.message}>{message}</p>}

      {showForm && (
        <div style={quizStyles.formCard}>
          <div style={quizStyles.typeRow}>
            <button
              style={form.question_type === 'multiple_choice' ?
                quizStyles.typeBtnActive : quizStyles.typeBtn}
              onClick={() => handleTypeChange('multiple_choice')}
            >
              Multiple Choice
            </button>
            <button
              style={form.question_type === 'true_false' ?
                quizStyles.typeBtnActive : quizStyles.typeBtn}
              onClick={() => handleTypeChange('true_false')}
            >
              True / False
            </button>
          </div>

          <textarea
            style={quizStyles.questionInput}
            placeholder="Enter your question..."
            value={form.question_text}
            onChange={e => setForm({...form, question_text: e.target.value})}
            rows={3}
          />

          <p style={quizStyles.answersLabel}>
            Answer Options — click the circle to mark correct answer
          </p>

          {form.answers.map((answer, index) => (
            <div key={index} style={quizStyles.answerRow}>
              <button
                style={answer.is_correct ?
                  quizStyles.correctCircle : quizStyles.circle}
                onClick={() => handleCorrectAnswer(index)}
              >
                {answer.is_correct ? '✓' : ''}
              </button>
              {form.question_type === 'true_false' ? (
                <span style={quizStyles.trueFalseLabel}>{answer.answer_text}</span>
              ) : (
                <input
                  style={quizStyles.answerInput}
                  placeholder={`Option ${index + 1}`}
                  value={answer.answer_text}
                  onChange={e => {
                    const updated = [...form.answers];
                    updated[index] = { ...updated[index], answer_text: e.target.value };
                    setForm({ ...form, answers: updated });
                  }}
                />
              )}
            </div>
          ))}

          <div style={quizStyles.formActions}>
            <button style={quizStyles.saveBtn} onClick={handleSaveQuestion}>
              {editingQuestion ? 'Update Question' : 'Save Question'}
            </button>
            <button
              style={quizStyles.cancelBtn}
              onClick={() => { setShowForm(false); resetForm(); }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading && <p style={quizStyles.emptyText}>Loading...</p>}

      {!loading && questions.length === 0 && !showForm && (
        <p style={quizStyles.emptyText}>
          No questions yet. Add your first question above.
        </p>
      )}

      {questions.map((q, index) => (
        <div key={q.id} style={quizStyles.questionCard}>
          <div style={quizStyles.questionHeader}>
            <span style={quizStyles.questionNum}>Q{index + 1}</span>
            <p style={quizStyles.questionText}>{q.question_text}</p>
            <div style={quizStyles.questionActions}>
              <button
                style={quizStyles.editBtn}
                onClick={() => handleEditQuestion(q)}
              >
                Edit
              </button>
              <button
                style={quizStyles.deleteBtn}
                onClick={() => handleDeleteQuestion(q.id)}
              >
                Delete
              </button>
            </div>
          </div>
          <div style={quizStyles.answersList}>
            {q.answers && q.answers.filter(a => a.id).map(answer => (
              <div key={answer.id} style={quizStyles.answerItem}>
                <span style={answer.is_correct ?
                  quizStyles.correctDot : quizStyles.dot} />
                <span style={{
                  ...quizStyles.answerText,
                  color: answer.is_correct ? '#1E8449' : '#ffffffcc',
                  fontWeight: answer.is_correct ? '600' : '400',
                }}>
                  {answer.answer_text}
                  {answer.is_correct && ' ✓'}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const quizStyles = {
  container: { backgroundColor: '#0a1220', border: '1px solid #C49A2830', borderRadius: '8px', padding: '16px', marginTop: '12px' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' },
  title: { color: '#C49A28', fontSize: '14px', fontWeight: '700', margin: '0' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
  passingScoreRow: { display: 'flex', alignItems: 'center', gap: '6px' },
  passingLabel: { color: '#ffffff60', fontSize: '12px' },
  passingInput: { backgroundColor: '#1A2744', border: '1px solid #ffffff20', borderRadius: '4px', padding: '4px 8px', color: '#ffffff', fontSize: '13px', outline: 'none', width: '50px', textAlign: 'center' },
  passingPct: { color: '#ffffff60', fontSize: '12px' },
  savePassingBtn: { backgroundColor: '#1A6B8A', color: '#ffffff', border: 'none', borderRadius: '4px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer' },
  addBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '6px', padding: '6px 14px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  message: { color: '#1E8449', fontSize: '12px', margin: '0 0 8px 0' },
  formCard: { backgroundColor: '#1A2744', borderRadius: '8px', padding: '16px', marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '10px' },
  typeRow: { display: 'flex', gap: '8px' },
  typeBtn: { backgroundColor: 'transparent', border: '1px solid #ffffff20', color: '#ffffff60', borderRadius: '6px', padding: '6px 14px', fontSize: '12px', cursor: 'pointer' },
  typeBtnActive: { backgroundColor: '#C49A28', border: '1px solid #C49A28', color: '#1A2744', borderRadius: '6px', padding: '6px 14px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  questionInput: { backgroundColor: '#0f1a33', border: '1px solid #ffffff20', borderRadius: '6px', padding: '10px', color: '#ffffff', fontSize: '13px', outline: 'none', resize: 'vertical', width: '100%', boxSizing: 'border-box' },
  answersLabel: { color: '#ffffff50', fontSize: '11px', margin: '0' },
  answerRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  circle: { width: '24px', height: '24px', borderRadius: '50%', border: '2px solid #ffffff30', backgroundColor: 'transparent', cursor: 'pointer', flexShrink: 0, color: 'transparent' },
  correctCircle: { width: '24px', height: '24px', borderRadius: '50%', border: '2px solid #1E8449', backgroundColor: '#1E8449', cursor: 'pointer', flexShrink: 0, color: '#ffffff', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  answerInput: { flex: 1, backgroundColor: '#0f1a33', border: '1px solid #ffffff20', borderRadius: '6px', padding: '8px 10px', color: '#ffffff', fontSize: '13px', outline: 'none' },
  trueFalseLabel: { color: '#ffffff', fontSize: '13px' },
  formActions: { display: 'flex', gap: '8px' },
  saveBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  cancelBtn: { backgroundColor: 'transparent', border: '1px solid #ffffff20', color: '#ffffff60', borderRadius: '6px', padding: '8px 12px', fontSize: '12px', cursor: 'pointer' },
  emptyText: { color: '#ffffff30', fontSize: '12px', margin: '0' },
  questionCard: { backgroundColor: '#1A2744', borderRadius: '6px', padding: '12px', marginBottom: '8px' },
  questionHeader: { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '8px' },
  questionNum: { backgroundColor: '#C49A2820', color: '#C49A28', fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '4px', flexShrink: 0 },
  questionText: { color: '#ffffff', fontSize: '13px', fontWeight: '600', margin: '0', flex: 1, lineHeight: '1.4' },
  questionActions: { display: 'flex', gap: '6px', flexShrink: 0 },
  editBtn: { backgroundColor: 'transparent', border: '1px solid #ffffff20', color: '#ffffff60', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', cursor: 'pointer' },
  deleteBtn: { backgroundColor: 'transparent', border: '1px solid #ff000030', color: '#ff6666', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', cursor: 'pointer' },
  answersList: { display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '8px' },
  answerItem: { display: 'flex', alignItems: 'center', gap: '8px' },
  dot: { width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#ffffff20', flexShrink: 0 },
  correctDot: { width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#1E8449', flexShrink: 0 },
  answerText: { fontSize: '12px' },
};
export default AdminCourseManager;